/**
 * Reads the board for how close the stack is to the top, and turns that into
 * a music-layer decision. Both directions here are read-only:
 * `boardDangerFraction` only reads `GameState.board`, never mutates it, and
 * neither function touches the fixed-timestep loop's simulation path — the
 * owner's overhaul brief: "purely presentational, zero engine involvement."
 */
import type { GameState } from '@interlock/engine';

import type { MusicLayer } from './sequencer.js';

/**
 * `0` when the visible board is empty, approaching `1` as the highest filled
 * cell nears the top of the visible playfield. Only the *visible* rows count
 * — the hidden spawn buffer above them is not something a player watching
 * their own board perceives as "the stack is high."
 */
export function boardDangerFraction(board: GameState['board']): number {
  for (let y = board.hiddenRows; y < board.height; y++) {
    const row = board.rows[y];
    if (row === undefined) continue;
    for (let x = 0; x < board.width; x++) {
      const cell = row[x];
      if (cell !== undefined && cell !== null) {
        const rowsFromTop = y - board.hiddenRows;
        return Math.max(0, Math.min(1, 1 - rowsFromTop / board.visibleRows));
      }
    }
  }
  return 0;
}

/** Enter the danger layer once the stack is genuinely high; leave it only once it has dropped well clear — hysteresis, so one piece wobbling near the line does not flicker the layer on and off. */
export const DANGER_ENTER_THRESHOLD = 0.72;
export const DANGER_EXIT_THRESHOLD = 0.55;

/**
 * Which music layer `dangerFraction` calls for, given the layer that is
 * currently playing. Pure — no `AudioContext`, no board, just the two
 * thresholds above — so the hysteresis itself is what
 * `test/audio/danger.test.ts` pins directly.
 */
export function selectMusicLayer(
  dangerFraction: number,
  currentLayer: MusicLayer = 'base',
): MusicLayer {
  if (currentLayer === 'danger') {
    return dangerFraction >= DANGER_EXIT_THRESHOLD ? 'danger' : 'base';
  }
  return dangerFraction >= DANGER_ENTER_THRESHOLD ? 'danger' : 'base';
}
